import { DateTime } from 'luxon'
import { memo, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import translationKeys from '@translationKeys'
import { Nullable } from '@types'

import CustomSlider from '@components/CustomSlider'
import { MEETING_STATUS_TO_COLOR_MAP, VISIBLE_DAYS_AMOUNT } from '@modules/contacts/constants'
import { Meeting, MeetingOverview, MeetingStatus } from '@modules/contacts/types'
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import Box from '@mui/material/Box'
import Skeleton from '@mui/material/Skeleton'
import Typography from '@mui/material/Typography'
import { useFeatureFlags } from '@shared/hooks/useFeatureFlags'

import ContactsOverviewCard from '../ContactsOverviewCard'
import DaysView from './DaysView'
import YearDropdown from './YearDropdown'
import {
  ContactHistoryWrapper,
  ContactsListWrapper,
  IconButtonContainer,
  LoadingWrapper,
  MeetingDot,
  MeetingDotsContainer,
  MonthItemContainer,
  SectionItemWrapper,
  SliderContainer,
  TabsCarouselWrapper,
} from './index.styles'
import { calculateMonthAndDayBySliderPosition, getMonthMeetings, months } from './utils'

/**
 * !! THIS COMPONENT IS IN PROCESS OF REFACTORING FROM THE PROTOTYPE,
 * IT REQUIRES A BUNCH OF OPTIMIZATIONS !!
 * */

const DateRangeSlider = ({
  sliderValue,
  onSliderValueChange,
  meetings,
  dayMeetings,
  selectedDate,
  onDateSelect,
  selectedYear,
  onYearSelect,
  isLoading,
  years,
  selectedMeeting,
  setSelectedMeeting,
  companyId,
}: {
  sliderValue: number
  onSliderValueChange: (value: number) => void
  meetings: MeetingOverview[]
  dayMeetings: Meeting[]
  selectedDate: string
  onDateSelect: (date: string) => void
  selectedYear: number
  onYearSelect: (year: number) => void
  isLoading?: boolean
  years: number[]
  selectedMeeting: Nullable<Meeting>
  setSelectedMeeting: (meeting: Meeting) => void
  companyId: number
}) => {
  const { t } = useTranslation()
  const handleSliderChange = (e: unknown, value: number | number[]) => {
    onSliderValueChange(value as number)
  }

  const { monthDate: sliderOnMonthDate, dayNum: sliderOnDayNum } = useMemo(
    () => calculateMonthAndDayBySliderPosition(sliderValue),
    [sliderValue],
  )
  const { shouldUpgradeToPayingUser } = useFeatureFlags()
  return (
    <ContactHistoryWrapper data-testid="contact-history-wrapper">
      <Box mb="10px" display="flex">
        <Typography variant="h2" sx={{ marginRight: '6px' }}>
          {`${t(
            shouldUpgradeToPayingUser
              ? translationKeys.MEETING_STATUS_HISTORY_TITLE_FOR_FREE_USER
              : translationKeys.MEETING_STATUS_HISTORY_TITLE_FOR_PREMIUM_USER,
          )}: `}
        </Typography>
        <YearDropdown selectedYear={selectedYear} years={years} onYearSelect={onYearSelect} disabled={years.length <= 1} />
      </Box>

      <SectionItemWrapper data-testid="slider-timeline-wrapper">
        <Box display="flex" justifyContent="center">
          {months.map((m, mIdx) => (
            <MonthItemContainer
              data-testid="month-slider"
              onClick={() => onSliderValueChange(mIdx * 30 + 15)}
              $selected={sliderOnMonthDate.toISO() === m.iso}
            >
              <Typography>{m.label}</Typography>
              <MeetingDotsContainer $selected={sliderOnMonthDate.toISO() === m.iso}>
                {getMonthMeetings(mIdx + 1, meetings, selectedYear).map((meeting, idx, arr) => (
                  <MeetingDot key={meeting.id} $color={MEETING_STATUS_TO_COLOR_MAP[meeting.status as MeetingStatus]?.bar} />
                ))}
              </MeetingDotsContainer>
            </MonthItemContainer>
          ))}
        </Box>

        <SliderContainer>
          <CustomSlider value={sliderValue} onChange={handleSliderChange} min={1} max={365} />
        </SliderContainer>
      </SectionItemWrapper>

      <SectionItemWrapper data-testid="month-timeline-wrapper">
        <Box display="flex" alignItems="center" justifyContent="space-between" mx="10px">
          <Typography variant="body1">{sliderOnMonthDate.monthShort}</Typography>
          {sliderOnMonthDate.daysInMonth! - sliderOnDayNum < VISIBLE_DAYS_AMOUNT && (
            <Typography variant="body1">{DateTime.fromObject({ month: sliderOnMonthDate.month + 1 }).monthShort}</Typography>
          )}
        </Box>

        <TabsCarouselWrapper>
          <IconButtonContainer
            onClick={() => onSliderValueChange(sliderValue - 1 > 0 ? sliderValue - 1 : 0)}
            sx={{ margin: '0 10px' }}
          >
            <KeyboardArrowLeftIcon color="primary" fontSize="small" />
          </IconButtonContainer>

          <DaysView
            month={sliderOnMonthDate.month}
            year={DateTime.fromISO(selectedDate).year}
            day={sliderOnDayNum}
            meetings={meetings}
            selectedDay={selectedDate}
            onDayClick={onDateSelect}
          />

          <IconButtonContainer
            onClick={() => onSliderValueChange(sliderValue + 1 < 365 ? sliderValue + 1 : 365)}
            sx={{ margin: '0 6px' }}
          >
            <KeyboardArrowRightIcon color="primary" fontSize="small" />
          </IconButtonContainer>
        </TabsCarouselWrapper>
      </SectionItemWrapper>
      <Box mb="20px" />
      {!isLoading && dayMeetings?.length === 1 ? (
        <ContactsOverviewCard
          companyId={companyId}
          singleViewMode
          meeting={dayMeetings[0]}
          hideDetailsBtn
          showSelection={false}
          isSelected
          setSelected={setSelectedMeeting}
        />
      ) : (
        <SectionItemWrapper data-testid="meetings-list-wrapper" sx={{ marginTop: '-1px', paddingRight: '8px' }}>
          {!isLoading ? (
            <ContactsListWrapper>
              {dayMeetings.map(m => (
                <ContactsOverviewCard
                  companyId={companyId}
                  meeting={m}
                  hideDetailsBtn
                  showSelection={dayMeetings.length > 1}
                  isSelected={selectedMeeting === m}
                  setSelected={setSelectedMeeting}
                />
              ))}
            </ContactsListWrapper>
          ) : (
            <LoadingWrapper>
              <Skeleton width="22px" />
              <Skeleton width="36px" />
              <Skeleton width="22px" />
              <Skeleton width="36px" />
              <Skeleton width="22px" />
            </LoadingWrapper>
          )}
        </SectionItemWrapper>
      )}
    </ContactHistoryWrapper>
  )
}

export default memo(DateRangeSlider)
